import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DateInput } from '../components/ui/date-input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, Search, MoreHorizontal, Pencil, Trash2, Phone, Wifi, ArrowUpDown, ArrowUp, ArrowDown, Download, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const contractTypeLabels = { internet_voce: 'Internet + Voce', mobile: 'Mobile', fisso: 'Fisso', fibra: 'Fibra', fwa_5g_sim: 'FWA 5G SIM' };
const contractTypeIcons = { internet_voce: Wifi, mobile: Phone, fisso: Phone, fibra: Wifi, fwa_5g_sim: Wifi };
const statusLabels = { attivo: 'Attivo', sospeso: 'Sospeso', cessato: 'Cessato' };
const paymentFrequencyLabels = { 
  mensile: 'Mensile', 
  bimestrale: 'Bimestrale', 
  semestrale: 'Semestrale', 
  annuo: 'Annuo' 
};

const emptyContract = {
  client_id: '',
  operator: '',
  contract_type: 'internet_voce',
  payment_frequency: 'mensile',
  start_date: '',
  end_date: '',
  monthly_cost: '',
  sconto: '',
  modem_type: 'operatore',
  phone_number: '',
  number_type: 'sim_mobile',
  status: 'attivo',
  notes: ''
};

const modemTypeLabels = {
  operatore: 'Fornito dall\'operatore',
  cliente: 'Fornito da noi al cliente'
};

const numberTypeLabels = {
  sim_mobile: 'Sim mobile',
  numero_fisso: 'Numero fisso'
};

const TelecomContracts = () => {
  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [formData, setFormData] = useState(emptyContract);
  const [costSort, setCostSort] = useState('none');
  const [dateSort, setDateSort] = useState('none');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [contractsRes, clientsRes] = await Promise.all([
        axios.get(`${API}/telecom/contracts`),
        axios.get(`${API}/clients`)
      ]);
      setContracts(contractsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { 
        ...formData, 
        monthly_cost: formData.monthly_cost ? parseFloat(formData.monthly_cost) : 0,
        sconto: formData.sconto ? parseFloat(formData.sconto) : 0
      };
      if (editingContract) {
        await axios.put(`${API}/telecom/contracts/${editingContract.id}`, payload);
        toast.success('Contratto aggiornato');
      } else {
        await axios.post(`${API}/telecom/contracts`, payload);
        toast.success('Contratto creato');
      }
      setDialogOpen(false);
      setEditingContract(null);
      setFormData(emptyContract);
      setClientSearch('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleEdit = (contract) => {
    setEditingContract(contract);
    setFormData({
      client_id: contract.client_id,
      operator: contract.operator || '',
      contract_type: contract.contract_type || 'internet_voce',
      payment_frequency: contract.payment_frequency || 'mensile',
      start_date: contract.start_date || '',
      end_date: contract.end_date || '',
      monthly_cost: contract.monthly_cost ? contract.monthly_cost.toString() : '',
      sconto: contract.sconto ? contract.sconto.toString() : '',
      modem_type: contract.modem_type || 'operatore',
      phone_number: contract.phone_number || '',
      number_type: contract.number_type || 'sim_mobile',
      status: contract.status || 'attivo',
      notes: contract.notes || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo contratto?')) return;
    try {
      await axios.delete(`${API}/telecom/contracts/${id}`);
      toast.success('Contratto eliminato');
      fetchData();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleDownloadPDF = async (contractId, clientName) => {
    try {
      toast.info('Generazione PDF in corso...');
      const response = await axios.get(`${API}/telecom/contracts/${contractId}/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Contratto_${clientName?.replace(/\s+/g, '_') || 'Cliente'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF scaricato!');
    } catch (error) {
      toast.error('Errore nella generazione del PDF');
    }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount || 0);

  const toggleCostSort = () => {
    setCostSort(prev => {
      if (prev === 'none') return 'asc';
      if (prev === 'asc') return 'desc';
      return 'none';
    });
    setDateSort('none');
  };

  const toggleDateSort = () => {
    setDateSort(prev => {
      if (prev === 'none') return 'asc';
      if (prev === 'asc') return 'desc';
      return 'none';
    });
    setCostSort('none');
  };

  // Filter clients for search in form
  const filteredClients = clients.filter(client => {
    if (!clientSearch) return true;
    const fullName = `${client.first_name} ${client.last_name}`.toLowerCase();
    return fullName.includes(clientSearch.toLowerCase());
  });

  const filteredContracts = contracts
    .filter(c => {
      const matchesSearch = `${c.client_name} ${c.operator || ''}`.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = filterType === 'all' || c.contract_type === filterType;
      const matchesStatus = filterStatus === 'all' || c.status === filterStatus;
      const matchesStartDate = !filterStartDate || (c.start_date && c.start_date.startsWith(filterStartDate));
      return matchesSearch && matchesType && matchesStatus && matchesStartDate;
    })
    .sort((a, b) => {
      if (costSort === 'asc') return (a.monthly_cost || 0) - (b.monthly_cost || 0);
      if (costSort === 'desc') return (b.monthly_cost || 0) - (a.monthly_cost || 0);
      if (dateSort === 'asc') return (a.start_date || '').localeCompare(b.start_date || '');
      if (dateSort === 'desc') return (b.start_date || '').localeCompare(a.start_date || '');
      return 0;
    });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('it-IT');
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    const classes = { attivo: 'bg-emerald-100 text-emerald-700', sospeso: 'bg-amber-100 text-amber-700', cessato: 'bg-slate-100 text-slate-700' };
    return <Badge className={classes[status]}>{statusLabels[status]}</Badge>;
  };

  const exportToExcel = () => {
    const headers = ['Cliente', 'Operatore', 'Tipo', 'Frequenza Pagamento', 'Inizio', 'Fine', 'Costo Mensile', 'Stato'];
    const rows = filteredContracts.map(c => [
      c.client_name || '',
      c.operator || '',
      contractTypeLabels[c.contract_type] || c.contract_type,
      paymentFrequencyLabels[c.payment_frequency] || c.payment_frequency || '',
      c.start_date ? new Date(c.start_date).toLocaleDateString('it-IT') : '',
      c.end_date ? new Date(c.end_date).toLocaleDateString('it-IT') : '',
      (c.monthly_cost || 0).toFixed(2).replace('.', ','),
      statusLabels[c.status] || c.status
    ]);
    const csvContent = [headers.join(';'), ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))].join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `contratti_telecom_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Esportazione completata');
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="telecom-contracts-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Contratti Telefonia</h1>
          <p className="text-slate-600 mt-1">Gestisci i contratti delle linee telefoniche</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportToExcel} disabled={contracts.length === 0} data-testid="export-contracts-btn">
            <Download className="w-4 h-4 mr-2" />Esporta Excel
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={() => { setEditingContract(null); setFormData(emptyContract); setClientSearch(''); }} data-testid="add-contract-btn">
                <Plus className="w-4 h-4 mr-2" />Nuovo Contratto
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingContract ? 'Modifica Contratto' : 'Nuovo Contratto'}</DialogTitle>
              <DialogDescription>Inserisci i dati del contratto</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Cliente</Label>
                <Input
                  placeholder="Cerca cliente per nome o cognome..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  className="mb-2"
                />
                <Select value={formData.client_id} onValueChange={(v) => setFormData({ ...formData, client_id: v })}>
                  <SelectTrigger data-testid="contract-client-select"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                  <SelectContent>
                    {filteredClients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Operatore</Label>
                  <Input value={formData.operator} onChange={(e) => setFormData({ ...formData, operator: e.target.value })} placeholder="Es: TIM, Vodafone..." data-testid="contract-operator" />
                </div>
                <div className="space-y-2">
                  <Label>Tipo Servizio</Label>
                  <Select value={formData.contract_type} onValueChange={(v) => setFormData({ ...formData, contract_type: v })}>
                    <SelectTrigger data-testid="contract-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(contractTypeLabels).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Modem/Router</Label>
                  <Select value={formData.modem_type} onValueChange={(v) => setFormData({ ...formData, modem_type: v })}>
                    <SelectTrigger data-testid="contract-modem-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="operatore">Fornito dall'operatore</SelectItem>
                      <SelectItem value="cliente">Fornito da noi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Pagamenti</Label>
                  <Select value={formData.payment_frequency} onValueChange={(v) => setFormData({ ...formData, payment_frequency: v })}>
                    <SelectTrigger data-testid="contract-payment-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(paymentFrequencyLabels).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Costo Mensile (€)</Label>
                  <Input type="number" step="0.01" value={formData.monthly_cost} onChange={(e) => setFormData({ ...formData, monthly_cost: e.target.value })} placeholder="0.00" data-testid="contract-cost" />
                </div>
                <div className="space-y-2">
                  <Label>Sconto (%)</Label>
                  <Input type="number" step="1" min="0" max="100" value={formData.sconto} onChange={(e) => setFormData({ ...formData, sconto: e.target.value })} placeholder="0" data-testid="contract-sconto" />
                </div>
                <div className="space-y-2">
                  <Label>Stato</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                    <SelectTrigger data-testid="contract-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(statusLabels).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {formData.monthly_cost && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-600 text-sm">Costo Mensile{formData.sconto > 0 ? ` (-${formData.sconto}% sconto)` : ''}:</p>
                      <p className="font-bold text-lg text-blue-800">
                        €{(formData.sconto > 0 
                          ? parseFloat(formData.monthly_cost) * (1 - parseFloat(formData.sconto) / 100) 
                          : parseFloat(formData.monthly_cost || 0)
                        ).toFixed(2)}/mese
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-blue-700 font-medium text-sm">
                        Totale Spesa ({paymentFrequencyLabels[formData.payment_frequency] || 'Mensile'}):
                      </p>
                      <p className="text-2xl font-bold text-blue-900">
                        €{(() => {
                          const costoBase = formData.sconto > 0 
                            ? parseFloat(formData.monthly_cost) * (1 - parseFloat(formData.sconto) / 100) 
                            : parseFloat(formData.monthly_cost || 0);
                          const moltiplicatore = {
                            'mensile': 1,
                            'bimestrale': 2,
                            'semestrale': 6,
                            'annuo': 12
                          }[formData.payment_frequency] || 1;
                          return (costoBase * moltiplicatore).toFixed(2);
                        })()}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Numero in servizio</Label>
                  <Input 
                    value={formData.phone_number} 
                    onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })} 
                    placeholder="Es: 333 1234567" 
                    data-testid="contract-phone" 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tipo Numero</Label>
                  <Select value={formData.number_type} onValueChange={(v) => setFormData({ ...formData, number_type: v })}>
                    <SelectTrigger data-testid="contract-number-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(numberTypeLabels).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data Inizio</Label>
                  <DateInput value={formData.start_date} onChange={(val) => setFormData({ ...formData, start_date: val })} data-testid="contract-start-date" placeholder="gg/mm/aaaa" />
                </div>
                <div className="space-y-2">
                  <Label>Data Fine</Label>
                  <DateInput value={formData.end_date} onChange={(val) => setFormData({ ...formData, end_date: val })} data-testid="contract-end-date" placeholder="gg/mm/aaaa" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Note</Label>
                <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} data-testid="contract-notes" />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); setClientSearch(''); }}>Annulla</Button>
                <Button type="submit" className="bg-violet-600 hover:bg-violet-700" data-testid="save-contract-btn">
                  {editingContract ? 'Salva Modifiche' : 'Crea Contratto'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Cerca per cliente, operatore..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-contracts" />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full sm:w-40" data-testid="filter-type">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i tipi</SelectItem>
                {Object.entries(contractTypeLabels).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-40" data-testid="filter-status">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                {Object.entries(statusLabels).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
              </SelectContent>
            </Select>
            <Input 
              type="month" 
              value={filterStartDate} 
              onChange={(e) => setFilterStartDate(e.target.value)} 
              className="w-full sm:w-40"
              placeholder="Data Inizio"
              data-testid="filter-start-date"
            />
          </div>
        </CardContent>
      </Card>

      {/* Dashboard Stats - Clickable for filtering */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${filterStatus === 'all' ? 'ring-2 ring-violet-500' : ''}`}
          onClick={() => setFilterStatus('all')}
        >
          <CardContent className="pt-4">
            <p className="text-sm text-slate-500">Totale Contratti</p>
            <p className="text-2xl font-bold text-violet-600">{contracts.length}</p>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${filterStatus === 'attivo' ? 'ring-2 ring-emerald-500' : ''}`}
          onClick={() => setFilterStatus('attivo')}
        >
          <CardContent className="pt-4">
            <p className="text-sm text-slate-500">Attivi</p>
            <p className="text-2xl font-bold text-emerald-600">{contracts.filter(c => c.status === 'attivo').length}</p>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${filterStatus === 'sospeso' ? 'ring-2 ring-amber-500' : ''}`}
          onClick={() => setFilterStatus('sospeso')}
        >
          <CardContent className="pt-4">
            <p className="text-sm text-slate-500">Sospesi</p>
            <p className="text-2xl font-bold text-amber-600">{contracts.filter(c => c.status === 'sospeso').length}</p>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${filterStatus === 'cessato' ? 'ring-2 ring-slate-500' : ''}`}
          onClick={() => setFilterStatus('cessato')}
        >
          <CardContent className="pt-4">
            <p className="text-sm text-slate-500">Cessati</p>
            <p className="text-2xl font-bold text-slate-600">{contracts.filter(c => c.status === 'cessato').length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Contracts Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Caricamento...</div>
          ) : filteredContracts.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              {searchTerm || filterType !== 'all' || filterStatus !== 'all' ? 'Nessun contratto trovato' : 'Nessun contratto registrato'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Operatore</TableHead>
                    <TableHead>
                      <button 
                        onClick={toggleDateSort}
                        className="inline-flex items-center gap-1 hover:text-violet-600 transition-colors font-medium"
                      >
                        Data Inizio
                        {dateSort === 'none' && <ArrowUpDown className="w-4 h-4 opacity-50" />}
                        {dateSort === 'asc' && <ArrowUp className="w-4 h-4 text-violet-600" />}
                        {dateSort === 'desc' && <ArrowDown className="w-4 h-4 text-violet-600" />}
                      </button>
                    </TableHead>
                    <TableHead>Pagamenti</TableHead>
                    <TableHead>
                      <button 
                        onClick={toggleCostSort}
                        className="inline-flex items-center gap-1 hover:text-violet-600 transition-colors font-medium"
                      >
                        Costo
                        {costSort === 'none' && <ArrowUpDown className="w-4 h-4 opacity-50" />}
                        {costSort === 'asc' && <ArrowUp className="w-4 h-4 text-violet-600" />}
                        {costSort === 'desc' && <ArrowDown className="w-4 h-4 text-violet-600" />}
                      </button>
                    </TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredContracts.map((contract) => {
                    const Icon = contractTypeIcons[contract.contract_type] || Phone;
                    return (
                      <TableRow key={contract.id} data-testid={`contract-row-${contract.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                              <Icon className="w-5 h-5 text-violet-600" />
                            </div>
                            <span className="text-slate-600">{contractTypeLabels[contract.contract_type]}</span>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">{contract.client_name}</TableCell>
                        <TableCell>{contract.operator || '-'}</TableCell>
                        <TableCell className="text-slate-600">{formatDate(contract.start_date)}</TableCell>
                        <TableCell>{paymentFrequencyLabels[contract.payment_frequency] || 'Mensile'}</TableCell>
                        <TableCell className="font-medium text-violet-600">{formatCurrency(contract.monthly_cost)}</TableCell>
                        <TableCell>{getStatusBadge(contract.status)}</TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`contract-menu-${contract.id}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleDownloadPDF(contract.id, contract.client_name)} data-testid={`contract-pdf-${contract.id}`}>
                                <FileText className="w-4 h-4 mr-2" />Stampa PDF
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleEdit(contract)}>
                                <Pencil className="w-4 h-4 mr-2" />Modifica
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDelete(contract.id)} className="text-red-600">
                                <Trash2 className="w-4 h-4 mr-2" />Elimina
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TelecomContracts;
